// Due Date Control — a MAJOR module (PATTERNS.md BP-14): Dashboard, Due Dates
// (assigned) and Templates are listing MODES under DueDateLayout; the record
// routes are SIBLINGS of the modes. Shape mirrored from src/ata/index.tsx.
//
//   <base>             → redirect to the remembered mode (localStorage)
//   <base>/dashboard   → DueDateDashboard
//   <base>/list        → DueDateList view="assigned"
//   <base>/templates   → DueDateList view="templates"
//   <base>/create      → DueDateDetail (sibling, outside the layout)
//   <base>/:id         → DueDateDetail (sibling, outside the layout)
//
// <base> is whichever alias App.tsx mounted (duedate, due_date_control,
// due-date-control, whiteboard) or the nav row's `to`; nothing here names it.
// The permission gates are exactly the ones the flat routes had: `create` is
// open, `:id` needs view-log or edit, every listing surface needs view-log.
import React, { useEffect, useState } from 'react'
import { AeronetViewProps } from '../types'
import { Navigate, Route, Routes } from 'react-router-dom'
import { getOriginalUrl } from '../utils/originalUrl'
import DueDateList from './DueDateList'
import DueDateDetail from './DueDateDetail'
import DueDateDashboard from './DueDateDashboard'
import DueDateLayout from './DueDateLayout'
import { readRememberedModeSegment } from './modes'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { useConfig } from '../hooks/useConfig'

const DueDate: React.FC<React.PropsWithChildren<AeronetViewProps>> = (props)  => {
  const { originalUrl: domain, ...otherProps } = props;
  const originalUrl = getOriginalUrl(domain, "function=due_date_control");

  const currentUser = useCurrentUser();
  const whiteboardConfig = useConfig("whiteboard");
  const [hasDueDateCreatePermission, setHasDueDateCreatePermission] = useState<boolean>(false);
  const [hasDueDateEditPermission, setHasDueDateEditPermission] = useState<boolean>(false);
  const [hasDueDateDeletePermission, setHasDueDateDeletePermission] = useState<boolean>(false);
  const [hasDueDatePermission, setHasDueDatePermission] = useState<boolean>(false);

  useEffect(() => {
    if (whiteboardConfig && currentUser) {
      // Roxen gates New Record with Edit (not Create).
      if (
        whiteboardConfig.Edit &&
        currentUser.permissions?.whiteboard &&
        currentUser.permissions.whiteboard.includes(whiteboardConfig.Edit)
      ) {
        setHasDueDateCreatePermission(true);
        setHasDueDateEditPermission(true);
      } else {
        setHasDueDateCreatePermission(false);
        setHasDueDateEditPermission(false);
      }

      if (
        whiteboardConfig.Delete &&
        currentUser.permissions?.whiteboard &&
        currentUser.permissions.whiteboard.includes(whiteboardConfig.Delete)
      ) {
        setHasDueDateDeletePermission(true);
      } else {
        setHasDueDateDeletePermission(false);
      }
      if (
        whiteboardConfig.can_view_module_log &&
        currentUser.permissions?.whiteboard &&
        currentUser.permissions.whiteboard.includes(whiteboardConfig.can_view_module_log)
      ) {
        setHasDueDatePermission(true);
      } else {
        setHasDueDatePermission(false);
      }
    }
  }, [currentUser, whiteboardConfig]);

  return (
    <Routes>
      <Route
        path="create"
        element={
          <DueDateDetail
            originalUrl={originalUrl}
            canEdit={hasDueDateEditPermission}
          />
        }
      />
      {(hasDueDatePermission || hasDueDateEditPermission) ? (
        <Route
          path=":id"
          element={
            <DueDateDetail
              originalUrl={originalUrl}
              canEdit={hasDueDateEditPermission}
            />
          }
        />
      ) : null}
      {hasDueDatePermission ? (
        <Route element={<DueDateLayout title={props.title} />}>
          <Route
            index
            element={<Navigate to={readRememberedModeSegment()} replace />}
          />
          <Route path="dashboard" element={<DueDateDashboard />} />
          <Route
            path="list"
            element={
              <DueDateList
                view="assigned"
                canCreateDuedate={hasDueDateCreatePermission}
                canEditDuedate={hasDueDateEditPermission}
                canDeleteDuedate={hasDueDateDeletePermission}
                originalUrl={originalUrl}
                {...otherProps}
              />
            }
          />
          <Route
            path="templates"
            element={
              <DueDateList
                view="templates"
                canCreateDuedate={hasDueDateCreatePermission}
                canEditDuedate={hasDueDateEditPermission}
                canDeleteDuedate={hasDueDateDeletePermission}
                originalUrl={originalUrl}
                {...otherProps}
              />
            }
          />
        </Route>
      ) : null}
      {hasDueDatePermission ? (
        <Route
          path="*"
          element={<Navigate to={readRememberedModeSegment()} replace />}
        />
      ) : null}
    </Routes>
  )
}

export default DueDate